"use client";

import { createClient } from "@/utils/supabase/client";
import { useCallback, useEffect, useState } from "react";
import {
  parseDisplayPreferences,
  toDisplayPreferencesMetadata,
  type DisplayPreferences,
} from "../_logic/display-preferences";

/** Shape returned by {@link useDisplayPreferences}. */
export interface UseDisplayPreferencesResult {
  /** Fields the user has explicitly saved. Empty while loading, signed out, or never set. */
  saved: Partial<DisplayPreferences>;
  /** Whether the saved preferences have been read from the session yet. */
  isLoaded: boolean;
  /** Whether `saved` reflects a save made after mount (`USER_UPDATED`), not just the cached session. */
  isUpdated: boolean;
  /** Persists the preferences to the signed-in user's auth metadata. */
  save: (preferences: DisplayPreferences) => Promise<{ success: boolean }>;
}

/**
 * The signed-in user's date format and time zone preferences, stored in Supabase auth
 * `user_metadata` until `nowadays.users` has dedicated columns. Stays in sync across mounted
 * consumers through the auth `USER_UPDATED` event.
 *
 * @returns Saved preferences and a saver.
 */
export function useDisplayPreferences(): UseDisplayPreferencesResult {
  // States
  const [saved, setSaved] = useState<Partial<DisplayPreferences>>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  // Handlers
  const save = useCallback(async (preferences: DisplayPreferences) => {
    const { error } = await createClient().auth.updateUser({ data: toDisplayPreferencesMetadata(preferences) });
    if (error) {
      console.error("useDisplayPreferences: save failed", error);
      return { success: false };
    }
    setSaved(preferences);
    setIsUpdated(true);
    return { success: true };
  }, []);

  // Effects
  useEffect(() => {
    const supabase = createClient();
    let isActive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!isActive) return;
      setSaved(parseDisplayPreferences(data.session?.user.user_metadata));
      setIsLoaded(true);
    });
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      setSaved(parseDisplayPreferences(session?.user.user_metadata));
      if (event === "USER_UPDATED") setIsUpdated(true);
    });
    return () => {
      isActive = false;
      data.subscription.unsubscribe();
    };
  }, []);

  return { saved, isLoaded, isUpdated, save };
}
